import { useState } from "react";
import { companyDetails, images } from "../data";
import { getOptimizedImageUrl } from "../lib/utils";
import SEO from "../components/SEO";

export default function Contact() {
  const contactSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ContactPage",
        "name": "Kontakt Lederskab.nu",
        "description": "Kontakt Anita Lauridsen for en uforpligtende samtale om vores 12-måneders lederforløb.",
        "mainEntity": {
          "@type": "EducationalOrganization",
          "name": "Lederskab.nu",
          "telephone": "+4553876106",
          "email": "anita@stressfrikommunikation.dk",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Parkvej 25 3. th.",
            "addressLocality": "Vejle",
            "postalCode": "7100",
            "addressCountry": "DK"
          }
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Forside",
            "item": "https://lederskab.nu/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Kontakt",
            "item": "https://lederskab.nu/kontakt"
          }
        ]
      }
    ]
  };

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "" // Honeypot field
  });

  const [status, setStatus] = useState({
    type: null, // 'success' | 'error' | 'loading'
    message: ""
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "loading", message: "Sender din besked..." });

    // Client-side validation
    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setStatus({
        type: "error",
        message: "Venligst udfyld alle påkrævede felter."
      });
      return;
    }

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.status === "ok") {
        setStatus({
          type: "success",
          message: "Mange tak for din henvendelse! Jeg vender tilbage til dig hurtigst muligt."
        });
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: ""
        });
      } else {
        setStatus({
          type: "error",
          message: result.message || "Der skete en fejl under afsendelsen. Prøv venligst igen."
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus({
        type: "error",
        message: "Forbindelsesfejl. Kontroller din internetforbindelse og prøv igen."
      });
    }
  };

  return (
    <div className="bg-white">
      <SEO
        title="Kontakt Anita Lauridsen | Book en Samtale | Lederskab.nu"
        description="Har du spørgsmål eller ønsker du en uforpligtende afklarende samtale om lederforløbet? Kontakt Anita Lauridsen i dag på tlf. 53 87 61 06."
        canonicalPath="/kontakt"
        structuredData={contactSchema}
      />
      {/* Header Banner */}
      <section className="relative bg-gradient-to-br from-maersk-dark to-navy-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.15),transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-left">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange bg-brand-orange/20 border border-brand-orange/30 px-3 py-1.5 rounded-full">
              Kontakt Os
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mt-4 mb-6 leading-tight">
              Book en uforpligtende afklaringssamtale
            </h1>
            <p className="text-gray-300 text-lg sm:text-xl leading-relaxed">
              Har du spørgsmål til vores 12-måneders lederforløb, eller ønsker du en uforpligtende samtale om, hvordan vi kan styrke dit eller din virksomheds lederskab? Skriv eller ring til os i dag.
            </p>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            {/* Left Column: Her Image (col-span-4 on desktop) */}
            <div className="lg:col-span-4">
              <div className="relative bg-white p-0 overflow-hidden rounded-2xl shadow-xl border border-maersk-light h-full min-h-[400px] lg:min-h-[600px]">
                <img
                  src={getOptimizedImageUrl(images.herContact, "width=800,quality=85,format=auto")}
                  alt="Anita Lauridsen - Strategisk partner i ledelse og arbejdsmiljø"
                  width="800"
                  height="600"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover absolute inset-0"
                />
              </div>
            </div>

            {/* Right Column: Contact Info & Form (col-span-8 on desktop) */}
            <div className="lg:col-span-8 flex flex-col gap-8">
              {/* Contact Card */}
              <div className="bg-maersk-light p-6 rounded-2xl border border-maersk-blue/10 space-y-5">
                <h3 className="font-display text-xl font-bold text-maersk-dark">
                  Kontaktoplysninger
                </h3>
                
                <div className="space-y-3">
                  {/* Row 1: Adresse & Telefon */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Adresse */}
                    <div className="flex items-center gap-3 bg-white/60 p-3 rounded-xl border border-maersk-blue/5">
                      <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-maersk-blue shrink-0 shadow-xs text-sm">
                        <i className="fa-solid fa-location-dot" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase leading-none">Adresse</p>
                        <p className="font-semibold text-maersk-dark text-xs sm:text-sm mt-1">{companyDetails.address}</p>
                      </div>
                    </div>

                    {/* Telefon */}
                    <div className="flex items-center gap-3 bg-white/60 p-3 rounded-xl border border-maersk-blue/5">
                      <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-maersk-blue shrink-0 shadow-xs text-sm">
                        <i className="fa-solid fa-phone" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase leading-none">Telefon</p>
                        <a
                          href={`tel:${companyDetails.phone.replace(/\s+/g, "")}`}
                          className="font-semibold text-maersk-dark text-xs sm:text-sm hover:text-maersk-blue transition-colors duration-300 mt-1 block whitespace-nowrap"
                        >
                          {companyDetails.phone}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: E-mail */}
                  <div className="flex items-center gap-3 bg-white/60 p-3 rounded-xl border border-maersk-blue/5">
                    <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-maersk-blue shrink-0 shadow-xs text-sm">
                      <i className="fa-solid fa-envelope" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase leading-none">E-mail</p>
                      <a
                        href={`mailto:${companyDetails.email}`}
                        className="font-semibold text-maersk-dark text-xs sm:text-sm hover:text-maersk-blue transition-colors duration-300 mt-1 block"
                      >
                        {companyDetails.email}
                      </a>
                    </div>
                  </div>

                  {/* Row 3: Socials */}
                  <div className="flex items-center justify-between bg-white/60 p-3 rounded-xl border border-maersk-blue/5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-maersk-blue shrink-0 shadow-xs text-sm">
                        <i className="fa-solid fa-share-nodes" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase leading-none">Sociale medier</p>
                        <p className="font-semibold text-maersk-dark text-xs sm:text-sm mt-1">Følg med på sociale medier</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={companyDetails.socials.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-maersk-blue hover:bg-maersk-blue hover:text-white transition-all duration-300 shadow-xs text-sm"
                        aria-label="Facebook"
                      >
                        <i className="fa-brands fa-facebook-f" />
                      </a>
                      <a
                        href={companyDetails.socials.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-maersk-blue hover:bg-maersk-blue hover:text-white transition-all duration-300 shadow-xs text-sm"
                        aria-label="LinkedIn"
                      >
                        <i className="fa-brands fa-linkedin-in" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white p-8 sm:p-10 rounded-2xl border border-maersk-blue/10 shadow-lg text-left">
                <h3 className="font-display text-2xl font-bold text-maersk-dark mb-2">
                  Anmod om en afklaringssamtale
                </h3>
                <p className="text-navy-700 text-sm mb-8">
                  Udfyld formularen nedenfor med dine kontaktoplysninger, så vender vi tilbage til dig inden for 24 timer på hverdage for at aftale en tid.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot Field (ABSOLUTELY HIDDEN from humans) */}
                  <div className="sr-only absolute -top-96 -left-96">
                    <label htmlFor="website_url">Do not fill this out if you are a human</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Name Field */}
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-maersk-dark">
                        Navn <span className="text-brand-orange">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Dit fulde navn"
                        className="w-full px-4 py-3 rounded-md border border-maersk-blue/20 focus:border-maersk-blue focus:ring-2 focus:ring-maersk-blue/10 outline-hidden transition-all duration-300 text-sm"
                      />
                    </div>

                    {/* Phone Field */}
                    <div className="space-y-2">
                      <label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-maersk-dark">
                        Telefonnummer <span className="text-brand-orange">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Dit telefonnummer"
                        className="w-full px-4 py-3 rounded-md border border-maersk-blue/20 focus:border-maersk-blue focus:ring-2 focus:ring-maersk-blue/10 outline-hidden transition-all duration-300 text-sm"
                      />
                    </div>
                  </div>

                  {/* Email Field */}
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-maersk-dark">
                      E-mailadresse <span className="text-brand-orange">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Din e-mailadresse"
                      className="w-full px-4 py-3 rounded-md border border-maersk-blue/20 focus:border-maersk-blue focus:ring-2 focus:ring-maersk-blue/10 outline-hidden transition-all duration-300 text-sm"
                    />
                  </div>

                  {/* Description Field */}
                  <div className="space-y-2">
                    <label htmlFor="description" className="text-xs font-bold uppercase tracking-wider text-maersk-dark">
                      Beskrivelse / Hvad kan jeg hjælpe dig med? <span className="text-brand-orange">*</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      required
                      rows="5"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Skriv din besked her..."
                      className="w-full px-4 py-3 rounded-md border border-maersk-blue/20 focus:border-maersk-blue focus:ring-2 focus:ring-maersk-blue/10 outline-hidden transition-all duration-300 text-sm resize-none"
                    />
                  </div>

                  {/* Status Message */}
                  {status.type && (
                    <div
                      className={`p-4 rounded-md text-sm font-medium ${
                        status.type === "loading"
                          ? "bg-maersk-light text-maersk-blue"
                          : status.type === "success"
                          ? "bg-emerald-50 text-emerald-800 border border-emerald-100"
                          : "bg-rose-50 text-rose-800 border border-rose-100"
                      }`}
                    >
                      {status.type === "loading" && <i className="fa-solid fa-spinner animate-spin mr-2" />}
                      {status.message}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={status.type === "loading"}
                    className="w-full bg-brand-orange hover:bg-brand-orange-hover disabled:bg-gray-300 text-white py-4 rounded-md font-bold tracking-wide transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
                  >
                    Send besked <i className="fa-solid fa-paper-plane ml-2 text-xs" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
